/**
 * Task Service
 * API client for task management
 */

import { apiClient } from './api-client';
import type {
  Task,
  CreateTaskDto,
  UpdateTaskDto,
  CompleteTaskDto,
  CancelTaskDto,
  RejectTaskDto,
  TaskFilters,
  PaginationOptions,
  TaskStats,
  TaskComment,
  ApiResponse,
  PaginatedResponse
} from '../types/tasks';

export interface AvailableTaskUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
}

function buildTaskQueryParams(
  filters?: TaskFilters,
  pagination?: PaginationOptions
): URLSearchParams {
  const params = new URLSearchParams();
  const source = { ...filters, ...pagination };

  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== undefined && item !== null && String(item) !== '') {
          params.append(key, String(item));
        }
      });
      return;
    }

    params.set(key, String(value));
  });

  return params;
}

/**
 * Task Service
 */
export const taskService = {
  /**
   * Create a new task
   */
  async create(data: CreateTaskDto): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.post('/api/tasks', data);
    return response.data;
  },

  /**
   * Get all tasks with filters and pagination
   */
  async findAll(
    filters?: TaskFilters,
    pagination?: PaginationOptions
  ): Promise<ApiResponse<{ tasks: Task[]; pagination: PaginatedResponse<Task>['pagination'] }>> {
    const params = buildTaskQueryParams(filters, pagination);
    const response = await apiClient.get('/api/tasks', { params });
    return response.data;
  },

  /**
   * Get task by ID
   */
  async findById(id: number): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.get(`/api/tasks/${id}`);
    return response.data;
  },

  /**
   * Update task
   */
  async update(id: number, data: UpdateTaskDto): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.put(`/api/tasks/${id}`, data);
    return response.data;
  },

  /**
   * Complete task
   */
  async complete(id: number, data?: CompleteTaskDto): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.post(`/api/tasks/${id}/complete`, data || {});
    return response.data;
  },

  /**
   * Cancel task
   */
  async cancel(id: number, data: CancelTaskDto): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.post(`/api/tasks/${id}/cancel`, data);
    return response.data;
  },

  /**
   * Reject task with mandatory comment
   * This is an atomic operation that validates, adds comment, and changes status
   */
  async rejectTask(id: number, data: RejectTaskDto): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.patch(`/api/tasks/${id}/reject`, data);
    return response.data;
  },

  /**
   * Delete task (soft delete)
   */
  async delete(id: number): Promise<ApiResponse<void>> {
    const response = await apiClient.delete(`/api/tasks/${id}`);
    return response.data;
  },

  /**
   * Archive task (hide from main view immediately)
   */
  async archive(id: number): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.post(`/api/tasks/${id}/archive`);
    return response.data;
  },

  /**
   * Unarchive task (restore to main view)
   */
  async unarchive(id: number): Promise<ApiResponse<{ task: Task }>> {
    const response = await apiClient.post(`/api/tasks/${id}/unarchive`);
    return response.data;
  },

  /**
   * Get task statistics
   */
  async getStats(filters?: Pick<TaskFilters, 'assigned_user_id' | 'client_id'>): Promise<ApiResponse<{ stats: TaskStats }>> {
    const response = await apiClient.get('/api/tasks/stats', { params: filters });
    return response.data;
  },

  /**
   * Get tasks by client
   */
  async getByClient(
    clientId: string,
    pagination?: PaginationOptions
  ): Promise<ApiResponse<{ tasks: Task[]; pagination: PaginatedResponse<Task>['pagination'] }>> {
    const response = await apiClient.get(`/api/clients/${clientId}/tasks`, {
      params: pagination
    });
    return response.data;
  },

  /**
   * Get tasks by case
   */
  async getByCase(
    caseId: string,
    pagination?: PaginationOptions
  ): Promise<ApiResponse<{ tasks: Task[]; pagination: PaginatedResponse<Task>['pagination'] }>> {
    const response = await apiClient.get(`/api/cases/${caseId}/tasks`, {
      params: pagination
    });
    return response.data;
  },

  /**
   * Get tasks by interaction
   */
  async getByInteraction(
    interactionId: string,
    pagination?: PaginationOptions
  ): Promise<ApiResponse<{ tasks: Task[]; pagination: PaginatedResponse<Task>['pagination'] }>> {
    const response = await apiClient.get(`/api/interactions/${interactionId}/tasks`, {
      params: pagination
    });
    return response.data;
  },

  /**
   * Get task statistics for a specific client
   */
  async getClientTaskStats(clientId: string): Promise<ApiResponse<{ stats: TaskStats }>> {
    const response = await apiClient.get(`/api/clients/${clientId}/tasks/stats`);
    return response.data;
  },

  /**
   * Get available users for task assignment
   */
  async getAvailableUsers(): Promise<ApiResponse<AvailableTaskUser[]>> {
    const response = await apiClient.get('/api/auth/users');
    return response.data;
  },

  /**
   * Add comment to task
   */
  async addTaskComment(taskId: number, text: string): Promise<TaskComment> {
    const response = await apiClient.post(`/api/tasks/${taskId}/comments`, { text });
    return response.data.data.comment;
  },

  /**
   * Get comments for a task
   */
  async getTaskComments(taskId: number): Promise<TaskComment[]> {
    const response = await apiClient.get(`/api/tasks/${taskId}/comments`);
    return response.data.data.comments;
  }
};
